import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Me } from '../api/schemas';
import { initialen } from '../ui/elements';
import { cn } from '../ui/shadcn/utils';
import { FameSignet } from '../ui/FameSignet';
import { Wortmarke } from '../ui/Wortmarke';
import {
  kategorieAbzeichen,
  type Abzeichenstand,
  type NavEintrag,
  type NavKategorie,
  type NavPunkt,
} from './navigation';
import { toPath, type Route } from './router';
import type { Seitenspalte } from './seitenspalte-zustand';

/**
 * Die zweistufige Menüspalte — 244 px offen, 68 px eingeklappt.
 *
 * **Eine Kategorie ist ein `<button>` und kein Verweis.** Sie navigiert nicht,
 * sie klappt. Ein `<a>` ohne Ziel ist für ein Vorleseprogramm ein kaputter
 * Verweis — und `getByRole('link')` fände ihn, was genau die Rolle ist, mit der
 * neun Prüfstellen die Menüpunkte greifen. Der Klappzustand steht am Knopf als
 * `aria-expanded`; `aria-current="page"` bleibt am Unterpunkt, denn genau EINE
 * Seite ist offen.
 *
 * **Die Kategoriebeschriftung ist ausdrücklich KEINE Überschrift und kein
 * benannter Bereich.** `ui/elements.tsx` `Panel` erzeugt zu jedem Tafeltitel
 * gleichzeitig ein `<h2>` UND eine `<section aria-label>`; ein
 * `<h2>Buchhaltung</h2>` in der Spalte stünde damit neben jedem gleichnamigen
 * Tafeltitel, und in Playwrights strenger Eindeutigkeit ist ein zweiter Knoten
 * mit demselben Namen ein Fehlschlag und kein Treffer. Markiert wird deshalb
 * mit Farbe und Gewicht, nicht mit einer Rolle.
 *
 * **Unterhalb von `lg` bleibt es beim bisherigen Verhalten:** ein Block über
 * dem Inhalt, kein Einklappen, keine Schublade. Eine feste Spalte von 244 px
 * auf einem Telefon wäre keine Menüführung, sondern eine Wand — und ein
 * Hamburger-Knopf spart dort drei Zeilen Höhe und kostet dafür eine Fokusfalle
 * und die Frage, was beim Seitenwechsel mit dem offenen Menü geschieht. Für
 * eine Oberfläche, die an einem Schreibtisch bedient wird, ist das der
 * schlechtere Tausch.
 */
export function Sidebar(props: {
  readonly eintraege: readonly NavEintrag[];
  readonly aktiverPunkt: Route['name'];
  readonly aktiveKategorie: string | undefined;
  readonly zustand: Seitenspalte;
  readonly abzeichen: Abzeichenstand;
  readonly user: Me;
  readonly navigate: (route: Route) => void;
  /**
   * Der Firmenname der Installation für die Unterzeile im Kopf; fehlt er oder
   * ist er `null`, steht keine Unterzeile (`PF-marke`).
   */
  readonly firmenname?: string | null;
}): ReactNode {
  const eingeklappt = props.zustand.eingeklappt;

  return (
    <nav
      aria-label="Hauptnavigation"
      className={cn(
        'flex shrink-0 flex-col border-b border-border bg-card',
        // Ab `lg` die Spalte: volle Höhe, eigene Rollfläche, rechte Kante
        // statt unterer. Die Breite ist die einzige Bewegung im Rahmen
        // (0,18 s) — alles andere sind Farbwechsel.
        'lg:h-screen lg:border-b-0 lg:border-r',
        // Die Dauer steht als Eigenschaft und nicht als Kurzform mit dem
        // Namen `duration`: Der ist seit `tailwindcss-animate` mehrdeutig
        // (Übergang ODER Animation), und Tailwind warnt beim Bauen. Die
        // Eigenschaft direkt zu schreiben ist eindeutig und erzeugt dieselbe
        // Regel. Und der Hinweis nennt den mehrdeutigen Namen bewusst OHNE
        // seine eckigen Klammern — der Inhaltsleser von Tailwind liest auch
        // Kommentare und warnte sonst über diese Zeile.
        'lg:transition-[width] lg:ease-out lg:[transition-duration:180ms]',
        eingeklappt ? 'lg:w-[68px]' : 'lg:w-[244px]',
      )}
    >
      <Markenkopf eingeklappt={eingeklappt} firmenname={props.firmenname ?? null} />

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        <div className="flex flex-wrap gap-x-6 gap-y-1 lg:flex-col lg:flex-nowrap lg:gap-y-1">
          {props.eintraege.map((eintrag) =>
            eintrag.art === 'verweis' ? (
              <Verweiszeile
                key={eintrag.punkt.label}
                punkt={eintrag.punkt}
                aktiv={props.aktiverPunkt === eintrag.punkt.route.name}
                eingeklappt={eingeklappt}
                zahl={
                  eintrag.punkt.abzeichen === undefined
                    ? undefined
                    : props.abzeichen[eintrag.punkt.abzeichen]
                }
                navigate={props.navigate}
              />
            ) : (
              <Kategoriezeile
                key={eintrag.key}
                kategorie={eintrag}
                offen={!eingeklappt && props.zustand.offen.has(eintrag.key)}
                kindAktiv={props.aktiveKategorie === eintrag.key}
                aktiverPunkt={props.aktiverPunkt}
                eingeklappt={eingeklappt}
                abzeichen={props.abzeichen}
                umschalten={props.zustand.gruppeUmschalten}
                navigate={props.navigate}
              />
            ),
          )}
        </div>
      </div>

      <Anwenderzeile
        user={props.user}
        eingeklappt={eingeklappt}
        aktiv={props.aktiverPunkt === 'profile'}
        navigate={props.navigate}
      />
    </nav>
  );
}

/**
 * Kopf der Spalte: Signet und Wortmarke.
 *
 * **Die Wortmarke trägt jetzt Rollen und keine Palettenstufen mehr.** Sie stand
 * bis zu diesem Umbau als `text-fame-800 dark:text-fame-300` in `Shell.tsx` —
 * eine der letzten Stellen, an denen eine Datei die Zuordnung hell/dunkel von
 * Hand mitschrieb.
 *
 * **Seit `PF-signetbalken` (2026-10-15) steht links das Signet und nicht mehr
 * eine Kachel mit dem Buchstaben „F".** Die Kachel war ein Platzhalter aus der
 * Zeit vor dem Markenpaket; das Signet (`ui/FameSignet.tsx`, drei Balken) trägt
 * die Hell/Dunkel-Umschaltung selbst über die Rollen `--fa-signet-1` bis
 * `--fa-signet-3`.
 *
 * **`Markenkopf` und nicht `Wortmarke`, seit `W-wortmarke` (2026-08-28):** Der
 * Schriftzug selbst — nur er, ohne Kachel und ohne Unterzeile — ist in
 * `ui/Wortmarke.tsx` ausgelagert, weil ihn die Anmeldemaske (`LoginPage.tsx`)
 * mit einer eigenen Klassenliste ein zweites Mal trug. Diese Funktion hier
 * bleibt der lokale Rahmen aus Signet, Wortmarke und der Sidebar-eigenen
 * Unterzeile mit dem Firmennamen — die trägt `LoginPage.tsx` NICHT (dort steht
 * er an anderen Stellen), gehört also nicht in die gemeinsame Komponente.
 *
 * **Der Firmenname ist seit `PF-marke` (`E-1657`) ein Wert und keine
 * Zeichenkette mehr:** Er kommt vom Server (`useFirmenname()`, `app/marke.ts`)
 * — seit `PF-lizenzname` (`E-1731`) zuerst der Kundenname der hinterlegten
 * Lizenz, sonst der Name der geltenden Ausstellerfassung — und fehlt ganz —
 * keine Unterzeile —, wo die Installation keinen hat. Signet und
 * Wortmarke sind das Produkt, nicht die Firma, und bleiben.
 */
function Markenkopf(props: {
  readonly eingeklappt: boolean;
  readonly firmenname: string | null;
}): ReactNode {
  return (
    <div
      className={cn(
        'flex items-center gap-[11px] border-b border-border px-4 py-[13px]',
        props.eingeklappt && 'lg:justify-center lg:px-0',
      )}
    >
      <FameSignet className="size-8 shrink-0" />
      {props.eingeklappt ? null : (
        <span className="min-w-0 leading-tight">
          <Wortmarke className="block truncate text-sm text-foreground" />
          {props.firmenname === null ? null : (
            // Bis zu 200 Zeichen (Kundenname der Lizenz, `E-1731`): eine
            // Zeile mit Auslassung, der volle Name im `title`.
            <span className="block truncate text-[11px] text-text3" title={props.firmenname}>
              {props.firmenname}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/** Die gemeinsame Gestalt der ersten Stufe — Kategoriezeile wie direkter Verweis. */
const ERSTE_STUFE =
  'flex w-full items-center gap-[11px] rounded-lg px-[10px] py-[9px] text-[13.5px] transition-colors ' +
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card';

/**
 * Ein direkter Verweis auf der ersten Stufe.
 *
 * **Er trägt seit `KP-oberflaeche` (2026-09-17) ein Abzeichen, und das schliesst
 * eine gemessene Lücke.** `NavPunkt.abzeichen` gab es für Verweise der ersten
 * Stufe schon vorher — «Webhooks» führt es seit `WH-navabzeichen` (2026-09-10),
 * und `Shell.tsx` holte die Zahl auch —, aber diese Funktion nahm sie nie an
 * und zeigte sie nie: Die Zahl der erschöpften Zustellungen wurde bei jedem
 * Aufbau des Rahmens abgerufen und landete nirgends. Nur `Unterpunkt` (in einer
 * Kategorie) und `Kategoriezeile` (die Summe im zugeklappten Zustand) rendern
 * eine Zahl. Das war kein falscher Wert, sondern ein unsichtbarer — die
 * schlechtere Hälfte, weil kein Test und kein Wächter dagegen anschlagen kann.
 * `zahl` kommt deshalb hier an und geht durch dasselbe {@link Abzeichen} wie
 * beim Unterpunkt; `Shell.tsx` und `navigation.ts` bleiben unverändert, und
 * «Webhooks» bekommt seine Zahl damit im selben Zug.
 *
 * **In der schmalen Spalte bleibt sie stehen.** Die Beschriftung verschwindet
 * dort (`lg:hidden`), die Zahl nicht: Sie ist der Grund, warum jemand den Punkt
 * überhaupt aufsucht, und ein Sinnbild ohne sie sähe aus wie ein Punkt ohne
 * Arbeit.
 */
function Verweiszeile(props: {
  readonly punkt: NavPunkt;
  readonly aktiv: boolean;
  readonly eingeklappt: boolean;
  readonly zahl: number | undefined;
  readonly navigate: (route: Route) => void;
}): ReactNode {
  return (
    <a
      href={toPath(props.punkt.route)}
      aria-current={props.aktiv ? 'page' : undefined}
      // `title` NUR in der schmalen Spalte: Dort ist er der einzige Weg zum
      // zugänglichen Namen, weil die Beschriftung fehlt. In der breiten Spalte
      // wäre er ein Kurzhinweis, der wiederholt, was danebensteht.
      {...(props.eingeklappt ? { title: props.punkt.label } : {})}
      onClick={(event) => {
        // Kein vollständiger Seitenwechsel: Ein Neuladen holte das gesamte
        // Bündel neu und verlöre die Filter der Liste. Mit gedrückter
        // Zusatztaste bleibt es beim Verhalten des Browsers — «in neuem Tab
        // öffnen» soll funktionieren.
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        props.navigate(props.punkt.route);
      }}
      className={cn(
        ERSTE_STUFE,
        props.eingeklappt && 'lg:justify-center lg:px-0',
        props.aktiv
          ? 'bg-accent font-semibold text-primary'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      <props.punkt.icon aria-hidden="true" strokeWidth={1.7} className="size-[18px] shrink-0" />
      <span className={cn('min-w-0 flex-1 truncate', props.eingeklappt && 'lg:hidden')}>
        {props.punkt.label}
      </span>
      {props.zahl === undefined || props.zahl === 0 ? null : <Abzeichen wert={props.zahl} />}
    </a>
  );
}

/** Eine aufklappbare Kategorie samt ihrer Unterpunktliste. */
function Kategoriezeile(props: {
  readonly kategorie: NavKategorie;
  readonly offen: boolean;
  readonly kindAktiv: boolean;
  readonly aktiverPunkt: Route['name'];
  readonly eingeklappt: boolean;
  readonly abzeichen: Abzeichenstand;
  readonly umschalten: (key: string) => void;
  readonly navigate: (route: Route) => void;
}): ReactNode {
  // Die Summe erscheint nur zugeklappt. Offen sitzen die Zahlen an den
  // Unterpunkten, und eine Summe daneben zählte dasselbe zweimal.
  const summe = props.offen ? undefined : kategorieAbzeichen(props.kategorie, props.abzeichen);

  return (
    <div className="lg:w-full">
      <button
        type="button"
        aria-expanded={props.offen}
        onClick={() => {
          props.umschalten(props.kategorie.key);
        }}
        {...(props.eingeklappt ? { title: props.kategorie.label } : {})}
        className={cn(
          ERSTE_STUFE,
          props.eingeklappt && 'lg:justify-center lg:px-0',
          // Zu und ein Kind aktiv: die Kategorie trägt die Markierung
          // stellvertretend. Offen bleibt die Zeile durchsichtig, und nur die
          // Beschriftung nimmt die Hausfarbe an — sonst konkurrierten zwei
          // markierte Flächen übereinander um dieselbe Aussage.
          !props.offen && props.kindAktiv
            ? 'bg-accent font-semibold text-primary'
            : props.kindAktiv
              ? 'font-semibold text-primary hover:bg-secondary'
              : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        )}
      >
        <props.kategorie.icon
          aria-hidden="true"
          strokeWidth={1.7}
          className="size-[18px] shrink-0"
        />
        <span className={cn('min-w-0 flex-1 truncate text-left', props.eingeklappt && 'lg:hidden')}>
          {props.kategorie.label}
        </span>
        {summe === undefined || props.eingeklappt ? null : <Abzeichen wert={summe} />}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'size-[14px] shrink-0 text-text3 transition-transform duration-150',
            props.offen && 'rotate-180',
            props.eingeklappt && 'lg:hidden',
          )}
        />
      </button>

      {props.offen ? (
        <ul className="mb-1.5 ml-[27px] mt-0.5 space-y-px border-l border-border pl-[11px]">
          {props.kategorie.punkte.map((punkt) => (
            <li key={punkt.label}>
              <Unterpunkt
                punkt={punkt}
                aktiv={props.aktiverPunkt === punkt.route.name}
                zahl={punkt.abzeichen === undefined ? undefined : props.abzeichen[punkt.abzeichen]}
                navigate={props.navigate}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Ein Unterpunkt: Punktmarke, Beschriftung, gegebenenfalls Zahl. */
function Unterpunkt(props: {
  readonly punkt: NavPunkt;
  readonly aktiv: boolean;
  readonly zahl: number | undefined;
  readonly navigate: (route: Route) => void;
}): ReactNode {
  return (
    <a
      href={toPath(props.punkt.route)}
      aria-current={props.aktiv ? 'page' : undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        props.navigate(props.punkt.route);
      }}
      className={cn(
        'flex items-center gap-2 rounded-md px-[9px] py-[7px] text-[13px] transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
        props.aktiv
          ? 'bg-accent font-semibold text-primary'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {/* Die Punktmarke trägt keinen Text und damit nichts zum zugänglichen
          Namen bei — der bleibt genau «Eingangskorb», «Journal», «Perioden». */}
      <span
        aria-hidden="true"
        className={cn(
          'size-[5px] shrink-0 rounded-full',
          props.aktiv ? 'bg-primary' : 'bg-border-strong',
        )}
      />
      <span className="min-w-0 flex-1 truncate">{props.punkt.label}</span>
      {props.zahl === undefined || props.zahl === 0 ? null : <Abzeichen wert={props.zahl} />}
    </a>
  );
}

/**
 * Eine Zahl an einem Menüpunkt.
 *
 * **Sie steht INNERHALB des Verweises und ändert damit dessen zugänglichen
 * Namen** — aus «Eingangskorb» wird «Eingangskorb 12». Die neun Prüfstellen
 * greifen mit `getByRole('link', { name: 'Eingangskorb' })` ohne `exact`, und
 * das ist ein Teilzeichenkettenvergleich; sie finden ihn unverändert. Eine
 * Zahl, die per `aria-hidden` aus dem Namen fiele, wäre für ein
 * Vorleseprogramm gar nicht da — und sie ist der Grund, warum jemand den Punkt
 * überhaupt aufsucht.
 *
 * **`bg-warn-soft`/`text-warn` statt `bg-accent`/`text-primary` seit
 * `W-navbenennung` (2026-09-01).** Die Rückmeldung „Ihr sind zu wenig
 * Signalfarben" (2026-08-31) traf hier zu: Der Markenton (Lila) markiert in
 * dieser Oberfläche «hier bist Du» (siehe `--accent` in `styles.css`), nicht
 * einen Zustand — vier Rahmen-Zahlen (`eingangskorb`, `kreditorenerwartung`,
 * `dubletten`, `kontierungWartend`) sind aber ausnahmslos wartende Arbeit, die
 * derselben Rolle entspricht wie `versendet` im Beleg-Badge
 * (`ui/shadcn/badge.tsx`: `bg-warn-soft text-warn`). Eine Token-ROLLE und
 * keine Palettenstufe — `web:tokens` würde eine rohe `amber-*`/`orange-*`-Klasse
 * hier ablehnen.
 */
function Abzeichen(props: { readonly wert: number }): ReactNode {
  return (
    <span className="shrink-0 rounded-pille bg-warn-soft px-1.5 py-px text-[10.5px] font-semibold text-warn">
      {props.wert}
    </span>
  );
}

/**
 * Der Fuss: wer angemeldet ist — und seit `W-profilseite` (2026-08-31) der
 * Weg zur eigenen Profilseite (`/profil`).
 *
 * **Harte Bedingung: der Anzeigename muss OHNE Klick lesbar sein.**
 * `e2e/first-login.spec.ts` prüft `getByText('Erstanmeldung', { exact: true })`
 * sichtbar — das ist der Anzeigename des Anwenders und die Probe, mit der die
 * Erstanmeldung als abgeschlossen gilt. Der Name steht deshalb weiterhin als
 * Text im Klickbereich und nicht erst nach dem Öffnen eines Menüs.
 *
 * **Ein `<button>` und kein `<a>`.** Anders als bei den Menüpunkten in
 * `Verweiszeile`/`Unterpunkt` ist das hier kein Verweis, der eine eigene,
 * teilbare Adresse bräuchte, sondern ein Sprung zur eigenen Sitzungsecke —
 * dieselbe Erwägung wie bei den Kategoriezeilen (`Kategoriezeile` oben), die
 * ebenfalls `<button>` sind.
 *
 * **Kein USt-Widget und kein «Einstellungen».** Das Übergabepaket zeigt beides
 * im Fuss; hier gibt es weder eine Voranmeldung noch eine Einstellungsseite —
 * nur die Profilseite, mit eigenem Zweck (Passwort, letzte Anmeldungen).
 */
function Anwenderzeile(props: {
  readonly user: Me;
  readonly eingeklappt: boolean;
  readonly aktiv: boolean;
  readonly navigate: (route: Route) => void;
}): ReactNode {
  const kuerzel = initialen(props.user.displayName);
  return (
    <button
      type="button"
      aria-current={props.aktiv ? 'page' : undefined}
      onClick={() => {
        props.navigate({ name: 'profile' });
      }}
      className={cn(
        'flex w-full shrink-0 items-center gap-[11px] border-t border-border px-4 py-3 text-left',
        'transition-colors outline-none hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        props.eingeklappt && 'lg:justify-center lg:px-0',
      )}
      title={props.eingeklappt ? props.user.email : 'Zum eigenen Profil'}
    >
      <span
        aria-hidden="true"
        className="flex size-7 shrink-0 items-center justify-center rounded-pille bg-accent text-[11px] font-bold text-primary"
      >
        {kuerzel}
      </span>
      {props.eingeklappt ? null : (
        <span className="min-w-0 leading-tight">
          {/* Name und Rollen in getrennten Elementen: Zusammengelegt wären sie
              ein einziger Textknoten, und «wer ist angemeldet» liesse sich
              weder vorlesen noch prüfen, ohne die Rollen mitzulesen. */}
          <span className="block truncate text-[12.5px] font-medium text-foreground">
            {props.user.displayName}
          </span>
          {props.user.roles.length === 0 ? null : (
            <span className="block truncate text-[11px] text-text3">
              {props.user.roles.map((role) => ROLLE[role] ?? role).join(', ')}
            </span>
          )}
        </span>
      )}
    </button>
  );
}

/** Die deutschen Bezeichnungen der vier Rollen. */
const ROLLE: Readonly<Record<string, string>> = {
  admin: 'Administration',
  accountant: 'Buchhaltung',
  reviewer: 'Prüfung',
  readonly: 'Lesezugriff',
};
